export const WORKOUT_SECTION_TYPES = [
  "standard",
  "warmup",
  "cooldown",
  "amrap",
  "circuit",
  "interval",
  "emom",
  "for_time",
] as const;

export type WorkoutSectionType = (typeof WORKOUT_SECTION_TYPES)[number];

export const WORKOUT_SECTION_LABELS: Record<WorkoutSectionType, string> = {
  standard: "Standard",
  warmup: "Warm up",
  cooldown: "Cool down",
  amrap: "AMRAP",
  circuit: "Circuit",
  interval: "Interval",
  emom: "EMOM",
  for_time: "For time",
};

export const isWorkoutSectionType = (
  value: string | null | undefined,
): value is WorkoutSectionType =>
  Boolean(
    value && (WORKOUT_SECTION_TYPES as readonly string[]).includes(value),
  );

export const isLooseSection = (section: {
  section_type?: string | null;
  name?: string | null;
}) => section.section_type === "standard" && !section.name?.trim();

/** One default per workout: the first unnamed standard section, else the first section. */
export const soleDefaultSectionIndex = (
  sections: { section_type?: string | null; name?: string | null }[],
) => {
  const looseIndex = sections.findIndex((section) => isLooseSection(section));
  return looseIndex >= 0 ? looseIndex : 0;
};

const PLACEHOLDER_SECTION_NAME = /^section(?:\s+\d+)?$/i;

export const hasCustomSectionName = (section: {
  name?: string | null;
}) => {
  const named = section.name?.trim();
  return !!named && !PLACEHOLDER_SECTION_NAME.test(named);
};

export const sessionSectionLabel = (section: SessionSection) => {
  const typeLabel = isWorkoutSectionType(section.section_type)
    ? WORKOUT_SECTION_LABELS[section.section_type]
    : section.section_type;
  if (hasCustomSectionName(section)) {
    return section.name?.trim() || typeLabel || "Section";
  }
  return typeLabel || "Section";
};

export type SessionSection = {
  id: string;
  name: string | null;
  section_type: string;
  time_cap_seconds?: number | null;
  rounds?: number | null;
  interval_seconds?: number | null;
  work_seconds?: number | null;
  rest_seconds?: number | null;
  notes?: string | null;
};

export type SectionClockKind =
  | "none"
  | "countdown"
  | "countup"
  | "interval"
  | "rounds";

export type SectionClockEvent = "complete" | "interval" | "rest" | "work";

export type SectionClockState = {
  running: boolean;
  currentRound: number;
  elapsed: number;
  remaining: number;
  phase: "work" | "rest";
  lastTickAt?: number;
};

export type SessionBlockGroup<T> = {
  group: T;
  groupIndex: number;
};

export type SessionBlock<T> =
  | { kind: "loose"; groups: SessionBlockGroup<T>[] }
  | { kind: "section"; section: SessionSection; groups: SessionBlockGroup<T>[] };

export const toSessionSections = (
  sections:
    | Array<Partial<SessionSection> & { id?: string | null }>
    | null
    | undefined,
): SessionSection[] =>
  (sections ?? [])
    .filter(
      (section): section is Partial<SessionSection> & { id: string } =>
        Boolean(section.id),
    )
    .map((section) => ({
      id: section.id,
      name: section.name ?? null,
      section_type: section.section_type ?? "standard",
      time_cap_seconds: section.time_cap_seconds ?? null,
      rounds: section.rounds ?? null,
      interval_seconds: section.interval_seconds ?? null,
      work_seconds: section.work_seconds ?? null,
      rest_seconds: section.rest_seconds ?? null,
      notes: section.notes ?? null,
    }));

/**
 * Keep wraps when `sections[]` is missing but exercises still have
 * `section_id`. Synthesized rows are named so they are not treated as loose
 * unnamed-standard blocks. Prefer real template sections when present.
 */
export const sectionsForSession = <T extends { section_id?: string | null }>(
  sections: SessionSection[] | undefined,
  groups: T[][],
): SessionSection[] => {
  const resolved = toSessionSections(sections);
  const known = new Set(resolved.map((section) => section.id));
  const extras: SessionSection[] = [];
  for (const group of groups) {
    const id = group[0]?.section_id?.trim();
    if (!id || known.has(id)) continue;
    known.add(id);
    extras.push({
      id,
      name: "Section",
      section_type: "standard",
    });
  }
  return extras.length === 0 ? resolved : [...resolved, ...extras];
};

export const getSectionClockKind = (
  section: Pick<SessionSection, "section_type" | "time_cap_seconds">,
): SectionClockKind => {
  switch (section.section_type) {
    case "amrap":
      return section.time_cap_seconds && section.time_cap_seconds > 0
        ? "countdown"
        : "countup";
    case "for_time":
      return "countup";
    case "emom":
    case "interval":
      return "interval";
    case "circuit":
      return "rounds";
    default:
      return "none";
  }
};

export const sectionWorkSeconds = (section: SessionSection) => {
  if (section.section_type === "interval") {
    return Math.max(1, section.work_seconds ?? section.interval_seconds ?? 60);
  }
  return Math.max(1, section.interval_seconds ?? 60);
};

export const sectionRestSeconds = (section: SessionSection) =>
  Math.max(0, section.rest_seconds ?? 0);

export const defaultSectionClock = (
  section: SessionSection,
): SectionClockState => {
  const kind = getSectionClockKind(section);
  return {
    running: false,
    currentRound: 1,
    elapsed: 0,
    remaining:
      kind === "countdown"
        ? Math.max(0, section.time_cap_seconds ?? 0)
        : kind === "interval"
          ? sectionWorkSeconds(section)
          : 0,
    phase: "work",
  };
};

export const formatSectionClock = (totalSeconds: number) => {
  const clamped = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(clamped / 3600);
  const minutes = Math.floor((clamped % 3600) / 60);
  const seconds = clamped % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

export const clampSectionRound = (section: SessionSection, round: number) => {
  const kind = getSectionClockKind(section);
  const min = 1;
  const max =
    kind === "rounds" && section.rounds && section.rounds > 0
      ? section.rounds
      : Number.POSITIVE_INFINITY;
  return Math.min(max, Math.max(min, Math.round(round)));
};

export const sectionClockUnchanged = (
  left: SectionClockState,
  right: SectionClockState,
) =>
  left.remaining === right.remaining &&
  left.elapsed === right.elapsed &&
  left.phase === right.phase &&
  left.running === right.running &&
  left.currentRound === right.currentRound &&
  left.lastTickAt === right.lastTickAt;

const finishPhase = (
  clock: SectionClockState,
  section: SessionSection,
): { clock: SectionClockState; event: SectionClockEvent } => {
  const restSeconds = sectionRestSeconds(section);
  if (clock.phase === "work" && restSeconds > 0) {
    return {
      event: "rest",
      clock: {
        ...clock,
        phase: "rest",
        remaining: restSeconds,
      },
    };
  }

  const nextRound = clock.currentRound + 1;
  if (section.rounds && section.rounds > 0 && nextRound > section.rounds) {
    return {
      event: "complete",
      clock: {
        ...clock,
        running: false,
        remaining: 0,
        lastTickAt: undefined,
      },
    };
  }

  return {
    event: "work",
    clock: {
      ...clock,
      phase: "work",
      currentRound: nextRound,
      remaining: sectionWorkSeconds(section),
    },
  };
};

export const tickSectionClock = (
  clock: SectionClockState,
  section: SessionSection,
  now: number,
): { clock: SectionClockState; event?: SectionClockEvent } => {
  if (!clock.running) return { clock };

  const lastTickAt = clock.lastTickAt ?? now;
  let remainingTick = Math.floor((now - lastTickAt) / 1000);
  if (remainingTick <= 0) {
    return {
      clock: clock.lastTickAt ? clock : { ...clock, lastTickAt: now },
    };
  }

  const kind = getSectionClockKind(section);
  if (kind === "none" || kind === "rounds") {
    return { clock: { ...clock, running: false, lastTickAt: undefined } };
  }

  if (kind === "countup") {
    return {
      clock: {
        ...clock,
        elapsed: clock.elapsed + remainingTick,
        lastTickAt: now,
      },
    };
  }

  if (kind === "countdown") {
    const remaining = Math.max(0, clock.remaining - remainingTick);
    return {
      event: remaining === 0 ? "complete" : undefined,
      clock: {
        ...clock,
        remaining,
        running: remaining > 0,
        lastTickAt: remaining > 0 ? now : undefined,
      },
    };
  }

  let next: SectionClockState = { ...clock, lastTickAt: now };
  let event: SectionClockEvent | undefined;
  while (remainingTick > 0 && next.running) {
    if (next.remaining > remainingTick) {
      next = {
        ...next,
        remaining: next.remaining - remainingTick,
      };
      remainingTick = 0;
      break;
    }
    remainingTick -= Math.max(next.remaining, 0);
    const finished = finishPhase(next, section);
    next = { ...finished.clock, lastTickAt: now };
    event = finished.event;
    if (event === "complete") break;
  }

  return { clock: next, event };
};

export const isSameLoggerWrap = (
  sections: SessionSection[] | undefined,
  leftId?: string | null,
  rightId?: string | null,
) => {
  const left = leftId
    ? sections?.find((section) => section.id === leftId)
    : undefined;
  const right = rightId
    ? sections?.find((section) => section.id === rightId)
    : undefined;
  const wrapLeft = left != null && !isLooseSection(left);
  const wrapRight = right != null && !isLooseSection(right);
  if (!wrapLeft && !wrapRight) return true;
  return Boolean(wrapLeft && wrapRight && leftId === rightId);
};

export const groupSessionBlocks = <T extends { section_id?: string | null }[]>(
  groups: T[],
  sections: SessionSection[] | undefined,
): SessionBlock<T>[] => {
  const blocks: SessionBlock<T>[] = [];
  groups.forEach((group, groupIndex) => {
    const sectionId = group[0]?.section_id;
    const section = sectionId
      ? sections?.find((item) => item.id === sectionId)
      : undefined;
    const wrapped = section != null && !isLooseSection(section);
    const last = blocks[blocks.length - 1];

    if (wrapped && section) {
      if (last?.kind === "section" && last.section.id === section.id) {
        last.groups.push({ group, groupIndex });
        return;
      }
      blocks.push({
        kind: "section",
        section,
        groups: [{ group, groupIndex }],
      });
      return;
    }

    if (last?.kind === "loose") {
      last.groups.push({ group, groupIndex });
      return;
    }
    blocks.push({ kind: "loose", groups: [{ group, groupIndex }] });
  });
  return blocks;
};

export const sessionBlockContaining = <T extends { section_id?: string | null }[]>(
  groups: T[],
  sections: SessionSection[] | undefined,
  groupIndex: number,
) =>
  groupSessionBlocks(groups, sections).find((block) =>
    block.groups.some((item) => item.groupIndex === groupIndex),
  );

export const insertIndexForSection = <T extends { section_id?: string | null }[]>(
  groups: T[],
  sectionId?: string | null,
) => {
  if (!sectionId) return groups.length;
  let last = -1;
  groups.forEach((group, index) => {
    if (group[0]?.section_id === sectionId) last = index;
  });
  return last >= 0 ? last + 1 : groups.length;
};
